import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Case from './Case';
import { getCaseColumns, getCaseGridTemplate } from './columns';
import {
    getCaseViewerBinaryScopeId,
    useViewerBinaryCache,
    ViewerBinaryCacheProvider,
} from '../../viewer-cache';
import {
    Table,
    TableHead,
    TableBody,
} from '..';
import './styles.scss';

function CasesTable({
    cases,
    labs,
    onCaseStatusChange,
    onCaseLabChange,
}) {
    const viewerBinaryCache = useViewerBinaryCache();
    const { scope } = localStorage;
    const scopeArray = (scope || '').split(' ');
    const hasAccess = (rule) => scopeArray.includes(rule);
    const canViewClinicsColumn = hasAccess('cases.display.clinics:read');
    const canViewLabsColumn = hasAccess('cases.display.labs:read');
    const columns = getCaseColumns(canViewClinicsColumn, canViewLabsColumn);
    const gridTemplateColumns = getCaseGridTemplate(columns);

    // State for certain aspects of the case detail view.
    // This state is applied across all cases, so we have to keep it up here.
    const [caseDetailGlobalState, setCaseDetailGlobalState] = useState({
        leftPanel: 0, // For NonClearAligner cases, 0 = 3D, 1 = Media, 2 = Rx. Otherwise, 0 = Media, 2 = Rx
        isRightPanelOpen: true,
        rightPanel: 0, // 0 represents Actions, 1 represents Details
        showRenderTips: true,
    });

    const [openIndex, setOpenIndex] = useState(null);
    const checkIndex = (index) => {
        const currentCaseId = openIndex == null ? null : cases[openIndex]?.id;
        const nextCaseId = index == null ? null : cases[index]?.id;
        const currentScopeId = getCaseViewerBinaryScopeId(currentCaseId);
        const nextScopeId = getCaseViewerBinaryScopeId(nextCaseId);

        if (currentScopeId && currentScopeId !== nextScopeId) {
            viewerBinaryCache.abortInflight({ scopeId: currentScopeId });
        }

        if (index === null) {
            setOpenIndex(null);
            setCaseDetailGlobalState((currentState) => ({
                ...currentState,
                leftPanel: 0,
            }));
        } else setOpenIndex(index);
    };

    // If a case is opened, disable scroll on the containers behind it.
    // If closed, enable scroll on the containers behind it.
    useEffect(() => {
        document.body.style.overflow = openIndex === null ? 'auto' : 'hidden';
    }, [openIndex]);

    return (
        <Table>
            <TableHead>
                <div className="cases-grid-row cases-grid-head" style={{ gridTemplateColumns }}>
                    {columns.map((column) => (
                        <div className={`heading ${column.key === 'status' ? 'status-heading' : ''}`} key={column.key}>
                            {column.label}
                        </div>
                    ))}
                    <div className="heading heading-spacer" aria-hidden="true" />
                </div>
            </TableHead>
            <TableBody>
                {
                    cases.map((c, i) => (
                        <Case
                            {...c}
                            key={c.id}
                            numCases={cases.length}
                            index={i}
                            labs={labs}
                            open={openIndex != null && i === openIndex}
                            switchOpen={checkIndex}
                            caseDetailGlobalState={caseDetailGlobalState}
                            setCaseDetailGlobalState={setCaseDetailGlobalState}
                            onCaseStatusChange={onCaseStatusChange}
                            onCaseLabChange={onCaseLabChange}
                        />
                    ))
                }
            </TableBody>
        </Table>
    );
}

CasesTable.propTypes = {
    cases: PropTypes.arrayOf(PropTypes.shape),
    labs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
    })),
    onCaseStatusChange: PropTypes.func,
    onCaseLabChange: PropTypes.func,
};

CasesTable.defaultProps = {
    cases: [],
    labs: [],
    onCaseStatusChange: () => {},
    onCaseLabChange: () => {},
};

// TODO Rewrite this to use MUI table components instead of custom table components.
function Cases(props) {
    return (
        <ViewerBinaryCacheProvider>
            <CasesTable {...props} />
        </ViewerBinaryCacheProvider>
    );
}

Cases.propTypes = CasesTable.propTypes;
Cases.defaultProps = CasesTable.defaultProps;

export default Cases;
